import { ActionPicker } from '@adornis/action-picker/client/action-picker.js';
import '@adornis/action-picker/client/x-action-picker-host.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useEffect, useObservable } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { currentUser, currentUserSignal, impersonatingUser } from '@adornis/users/client/currentUser.js';
import { stopImpersonation } from '@adornis/users/client/impersonation.js';
import { createWikiActionPickerProvider } from '@adornis/wiki/client/action-picker-provider.js';
import { wikiRouter, XWiki } from '@adornis/wiki/client/x-wiki.js';
import { PageHeader } from '@martinus/styles/client/PageHeader.js';
import { html, nothing } from 'lit';
import { AccountingPage, accountingRouter } from '../accounting/client/AccountingPage.js';
import { AdminDashboardRouter, adminRouter } from '../admin-dashboard/client/AdminDashboardRouter.js';
import { getMartinusAuthentication } from '../authentication/martinus-authentication.js';
import { SGMassBookingEditor } from '../bookings/client/mass-booking-editor/sg-mass-booking-editor.js';
import { SGCalenderPage } from '../calender/client/sg-calender.js';
import { RouterPages } from '../db/enums/routes.js';
import { groupDetailRouter, SgGroupDetails } from '../groups/details/client/SgGroupDetails.js';
import { Group } from '../groups/entities/Group.js';
import { SGGroupsList } from '../groups/list-page/sg-groups-list.js';
import { useAbacEnabledPages } from '../permissions/client/useAbacEnabledPages.js';
import { PersonalQuittungenDownloadPage } from '../quittungen/client/PersonalQuittungenDownload.js';
import { SgDonations } from '../sepa/client/sg-donations-page.js';
import { SGInterviewSlotAdministration } from '../sus-entry-form/interviews/client/sg-interview-slot-administration.js';
import { SGSBConfirmPage } from '../sus-entry-form/registration-form/client/sg-sb-confirm-page.js';
import { SgSusEntryFormRegistrationForm } from '../sus-entry-form/registration-form/client/sg-sus-entry-form-registration-form.js';
import { SgSusEntryFormSchoolSelection } from '../sus-entry-form/school-selection/client/sg-sus-entry-form-school-selection.js';
import { SGTeachingAssistant } from '../teaching-assistant/client/sg-teaching-assistant.js';
import { changeMailRouter } from '../user-emails/change-email-address/sg-change-email-response-page.js';
import { personDetailRouter, SGUserDetails } from '../users/details/client/UserDetails.js';
import { SGEmployeeEntryForm } from '../users/employees/client/UserEmployeeEntryForm.js';
import { Person } from '../users/entities/person.js';
import { UserList } from '../users/list/client/UserList.js';
import { MainPages, mainPages, Sidebar } from './components/Sidebar.js';
import type { SgSnackbar } from './components/ui/sg-snackbar.js';
import { Datenschutz } from './pages/Datenschutz.js';
import { Impressum } from './pages/Impressum.js';
import { Login } from './pages/Login.js';
import { MainDashboard } from './pages/MainDashboard.js';
import { SGPasswordReset } from './pages/sg-password-reset.js';
import './pages/sg-standalone-ticket.js';

const authenticatedRoute = (page: Renderable) =>
  stateful(() => {
    return html`${authenticationGuard(
      getMartinusAuthentication().client,
      () => html`
        ${Sidebar({})}
        <div class="flex flex-col overflow-y-auto grow">${page}</div>
      `,
      Login({}),
      html`<div class="flex size-full items-center justify-center">
        <span class="loading loading-spinner loading-lg"></span>
      </div>`,
    )}`;
  })();

export const router = makeRouter(
  {
    root: { path: '/', render: () => authenticatedRoute(html`${MainDashboard()}`), documentTitlePart: 'Dashboard' },

    /**
     * Pages from the sidebar
     */
    [mainPages.kalender.label]: {
      path: mainPages.kalender.path,
      render: stateful(() => {
        const user = useObservable(() => currentUser(), []);
        return authenticatedRoute(SGCalenderPage({ personID: user?._id }));
      }),
    },

    [mainPages.person.label]: { path: mainPages.person.path, render: () => authenticatedRoute(UserList({})) },
    personenDetails: {
      path: '/personen/:personID{/*}?',
      render: params => authenticatedRoute(SGUserDetails({ personID: params.personID })),
      subRouter: personDetailRouter,
      // titles are computed even while authenticatedRoute shows the login page, where the lookup could only be
      // denied; reading the signal before the first await lets the title effect re-run after login
      documentTitlePart: async params => {
        if (!currentUserSignal.value) return 'Person';
        const person = await getByID(
          Person,
          params.personID,
        )({ profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } });
        const { vorname, nachname } = person?.profilDaten?.stammdaten ?? {};
        return [vorname, nachname].filter(Boolean).join(' ') || 'Person';
      },
    },

    [mainPages.wiki.label]: {
      path: `${mainPages.wiki.path}{/*}?`,
      render: () => authenticatedRoute(XWiki({})),
      subRouter: wikiRouter,
    },

    [mainPages.gruppen.label]: { path: mainPages.gruppen.path, render: () => authenticatedRoute(SGGroupsList()) },

    [mainPages.ticket.label]: {
      path: mainPages.ticket.path,
      render: () => authenticatedRoute(html` <sg-standalone-ticket></sg-standalone-ticket> `),
    },

    [mainPages.interviewAdministration.label]: {
      path: mainPages.interviewAdministration.path,
      render: () => authenticatedRoute(SGInterviewSlotAdministration()),
    },

    [mainPages.accounting.label]: {
      path: `${mainPages.accounting.path}{/*}?`,
      render: () => authenticatedRoute(AccountingPage()),
      subRouter: accountingRouter,
    },

    /**
     * TODO should refer to the person subpage for donations
     */
    spenden: {
      path: '/spenden',
      documentTitlePart: MainPages.MEINE_SPENDEN,
      render: stateful(() => {
        const user = useObservable(() => currentUser(), []);
        return authenticatedRoute(
          html` ${PageHeader({ title: 'Meine Spenden' })} ${user ? SgDonations({ personID: user._id }) : nothing}`,
        );
      }),
    },

    oneshotLogin: {
      path: '/login/:reroute',
      documentTitlePart: 'Anmeldung',
      render: params => html`
        <div class="flex flex-col w-screen items-center ">
          ${Login({
            onCompleted: () => {
              if (params.reroute) goTo(`${params.reroute}${document.location.search}`);
            },
          })}
        </div>
      `,
    },
    changePassword: {
      path: '/change-password',
      documentTitlePart: 'Passwort ändern',
      render: () => SGPasswordReset({ page: RouterPages.CHANGE_PASSWORT }),
    },
    changeEmail: {
      path: '/change-email{/*}?',
      documentTitlePart: 'E-Mail-Änderung',
      render: stateful(() => changeMailRouter.outlet.value),
      subRouter: changeMailRouter,
    },
    setPassword: {
      path: '/set-password',
      documentTitlePart: 'Passwort anlegen',
      render: () => SGPasswordReset({ page: RouterPages.SET_PASSWORT }),
    },
    datenschutz: { path: '/datenschutz', render: () => Datenschutz(), documentTitlePart: 'Datenschutz' },
    impressum: { path: '/impressum', render: () => Impressum(), documentTitlePart: 'Impressum' },

    eintrittsformular: {
      path: '/eintrittsformular',
      documentTitlePart: MainPages.EINTRITTSFORMULAR,
      render: stateful(() => {
        const user = useObservable(() => currentUser(), []);
        return authenticatedRoute(SGEmployeeEntryForm({ personID: user?._id }));
      }),
    },

    adminDashboard: {
      path: '/admin-dashboard{/*}?',
      documentTitlePart: MainPages.ADMIN_DASHBOARD,
      render: () => authenticatedRoute(AdminDashboardRouter()),
      subRouter: adminRouter,
    },
    bookings: {
      path: '/bookings',
      render: () => authenticatedRoute(SGMassBookingEditor()),
      documentTitlePart: 'Buchungen editieren',
    },

    groupDetails: {
      path: '/groups/:groupId{/*}?',
      render: params => authenticatedRoute(SgGroupDetails({ groupId: params.groupId })),
      subRouter: groupDetailRouter,
      // no lookup behind the login page, see personenDetails
      documentTitlePart: async params => {
        if (!currentUserSignal.value) return 'Gruppe';
        return (await getByID(Group, params.groupId)({ name: 1 }))?.name ?? 'Gruppe';
      },
    },
    quittungen: {
      path: '/quittungen',
      documentTitlePart: MainPages.QUITTUNGEN,
      render: stateful(() => {
        const user = useObservable(() => currentUser(), []);
        return authenticatedRoute(PersonalQuittungenDownloadPage({ userID: user?._id }));
      }),
    },

    susSchoolSelection: {
      path: '/sus-schulen',
      render: () => SgSusEntryFormSchoolSelection(),
      documentTitlePart: 'Schulauswahl',
    },
    susSchoolRegistration: {
      path: '/sus-register/:token',
      documentTitlePart: 'Bewerbung',
      render: params => SgSusEntryFormRegistrationForm({ token: params.token }),
    },
    susConfirm: {
      path: '/sus-register-sb-confirmed',
      render: () => SGSBConfirmPage(),
      documentTitlePart: 'Bestätigung',
    },
    teachingAssistant: {
      path: '/teaching-assistant',
      render: () => authenticatedRoute(SGTeachingAssistant()),
      documentTitlePart: MainPages.TEACHING_ASSISTANT,
    },
  },
  // the tab title suffix; replaces the one the server renders into index.html
  { routerDocumentTitle: 'Martinus Portal' },
);

router.init();

// If we change the page, hide the snackbar
router.currentRoute.subscribe(() => {
  const snackbarElement = document.querySelector<SgSnackbar>('sg-snackbar');
  if (snackbarElement) {
    void snackbarElement.hide();
  }
});

// TODO: On new Packagestructure => move to components
const ImpersonationWarning = () => {
  return html`
    <div class="w-full flex flex-row items-center justify-between bg-warning p-2 px-4">
      <div class="flex flex-row items-center gap-4">
        <span class="icon-[material-symbols--warning-rounded] size-5"></span>

        <span>Sie imitieren eine andere Person!</span>
      </div>
      <button
        class="btn btn-outline btn-error"
        @click=${async () => {
          await guardDialog(() => stopImpersonation(), {
            success: () => {
              goTo('/');
              location.reload();
            },
          });
        }}
      >
        <span class="icon-[material-symbols--close-rounded] size-5"></span>
        Imitieren beenden
      </button>
    </div>
  `;
};

export const MainElement = stateful(() => {
  const isImpersonating = useObservable(() => impersonatingUser(), []);
  const user = useObservable(() => currentUser<Person>(), []);
  const canSearchWiki = !!useAbacEnabledPages({ pages: [mainPages.wiki], viewedUser: user })?.length;

  // the wiki no longer registers its search itself, so the shell owns it for as long as the user may view the wiki
  useEffect(() => {
    if (!canSearchWiki) return;
    const picker = ActionPicker.global();
    const provider = createWikiActionPickerProvider();
    picker.setContext('wiki-search', [provider]);
    return () => {
      provider.setSearch('');
      picker.clearContext('wiki-search');
    };
  }, [canSearchWiki]);

  return html`
    <div class="flex flex-col h-screen w-screen">
      ${isImpersonating ? ImpersonationWarning() : nothing}

      <div class="flex w-screen flex-row grow min-h-0">${router.outlet.value}</div>
      ${canSearchWiki ? html`<x-action-picker-host placeholder="Wiki durchsuchen..."></x-action-picker-host>` : nothing}
    </div>
  `;
});
